/**
 * apiClient.js
 *
 * Industry-standard centralized Axios instance for this app.
 * - Auto-attaches Authorization header from Redux store (no need to pass authToken manually)
 * - Handles 401 (session expired → logout), 403 (access denied), 5xx (server errors)
 * - All API service files import this instead of raw axios
 *
 * Usage:
 *   import apiClient from 'services/apiClient';
 *   apiClient.get('/users').then(res => res.data)
 */

import axios from 'axios';
import { notification } from 'antd';
import store from 'store';
import { logout } from 'store/slices/authSlice';

const BASE_URL = process.env.REACT_APP_API_PATH;

// Create a single shared axios instance
const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout
});

// ─────────────────────────────────────────────────────────────────────────────
// REQUEST INTERCEPTOR
// Automatically reads token from Redux store and attaches it.
// No component ever needs to pass Authorization headers manually again.
// ─────────────────────────────────────────────────────────────────────────────
apiClient.interceptors.request.use(
  (config) => {
    // Read latest token from Redux store (works even after token refresh)
    const state = store.getState();
    const token = state?.auth?.token;

    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    // Let the browser set multipart boundary for file uploads.
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// ─────────────────────────────────────────────────────────────────────────────
// RESPONSE INTERCEPTOR
// Centralized error handling — no try/catch boilerplate in every component.
// ─────────────────────────────────────────────────────────────────────────────

// Track if a 401 logout is already in progress to avoid duplicate notifications
let isLoggingOut = false;

apiClient.interceptors.response.use(
  // Success: pass through as-is
  (response) => response,

  // Error: handle centrally
  (error) => {
    const status = error?.response?.status;
    // --- 401: Unauthorized / Session expired ---
    if (status === 401) {
      if (!isLoggingOut) {
        isLoggingOut = true;
        notification.warning({
          message: 'Session Expired',
          description: 'Your session has expired. Please log in again.',
          duration: 3,
        });
        // Dispatch logout and let the router handle redirect
        store.dispatch(logout());
        // Reset flag after a short delay
        setTimeout(() => { isLoggingOut = false; }, 3000);
      }
      return Promise.reject(error);
    }

    // --- 403: Forbidden / Access denied ---
    if (status === 403) {
      notification.error({
        message: 'Access Denied',
        description: 'You do not have permission to perform this action.',
        duration: 4,
      });
      return Promise.reject(error);
    }

    // --- 422: Validation errors (let components handle these) ---
    if (status === 422) {
      return Promise.reject(error);
    }

    // --- 5xx: Server errors ---
    if (status >= 500) {
      notification.error({
        message: 'Server Error',
        description: 'A server error occurred. Please try again later.',
        duration: 5,
      });
      return Promise.reject(error);
    }

    // --- Network/timeout errors ---
    if (!error.response) {
      notification.error({
        message: 'Connection Error',
        description: 'Unable to connect to the server. Please check your internet connection.',
        duration: 5,
      });
      return Promise.reject(error);
    }

    // --- All other errors: reject and let calling code handle ---
    return Promise.reject(error);
  }
);

export default apiClient;

/**
 * Coalesce identical in-flight GETs (React StrictMode remounts, parallel callers).
 * Concurrent callers share one promise; completed requests are not cached here.
 */
const inflightGets = new Map();

export function dedupedGet(url, config) {
  const key = typeof url === 'string' ? url : JSON.stringify(url);
  if (inflightGets.has(key)) {
    return inflightGets.get(key);
  }

  const promise = apiClient.get(url, config).finally(() => {
    inflightGets.delete(key);
  });
  inflightGets.set(key, promise);
  return promise;
}
